import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { getCurrentDomain } from "@root/utils/domain";
import { OrganizationService } from "@root/services/organization";

export interface OrganizationBranding {
  logoSignedUrl?: string | null;
  faviconSignedUrl?: string | null;
  certificateOrgName?: string | null;
  certificateOrgNameKr?: string | null;
  slug?: string | null;
  name?: string;
  domain?: string;
  [key: string]: unknown;
}

// Static loader favicon (SVG data URI) – shown in tab while org API is loading
const LOADER_FAVICON =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="none" stroke="%231819B4" stroke-width="3" stroke-dasharray="60 20" stroke-linecap="round"/></svg>'
  );

const getPublicUrl = (path: string) =>
  `${process.env.PUBLIC_URL || ""}/${path}`.replace(/\/+/g, "/") || `/${path}`;

const DEFAULT_LOGO_URL = getPublicUrl("DefaultLogo.png");
const DEFAULT_FAVICON_PATH = "DefaultFavIcon.png";

const defaultState: {
  branding: OrganizationBranding | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  defaultLogoUrl: string;
  slug: string | null;
  loading: boolean;
} = {
  branding: null,
  logoUrl: null,
  faviconUrl: null,
  defaultLogoUrl: DEFAULT_LOGO_URL,
  slug: null,
  loading: true,
};

const OrganizationBrandingContext =
  createContext<typeof defaultState>(defaultState);

interface OrganizationBrandingProviderProps {
  children: ReactNode;
}

export const OrganizationBrandingProvider: React.FC<
  OrganizationBrandingProviderProps
> = ({ children }) => {
  const [branding, setBranding] = useState<OrganizationBranding | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const defaultFaviconHref = getPublicUrl(DEFAULT_FAVICON_PATH);
    const defaultTitle = "KPEC 저널리즘 교육 플랫폼";

    const setFavicon = (href: string) => {
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = href;
    };

    const fetchBranding = async () => {
      // Remove default favicon and show loader favicon while API is loading
      const existingFavicon =
        document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      existingFavicon?.remove();
      setFavicon(LOADER_FAVICON);

      let faviconUrl: string | null = null;

      try {
        const domain = getCurrentDomain();
        const service = new OrganizationService();
        const response = await service.getOrganizationByDomain(domain);
        const data = response?.data?.data ?? response?.data;
        if (data) {
          setBranding(data);
          faviconUrl = data.faviconSignedUrl ?? null;
          if (data.browserTabName && String(data.browserTabName).trim()) {
            document.title = String(data.browserTabName).trim();
          }
        }
      } catch {
        document.title = defaultTitle;
      } finally {
        setLoading(false);
        if (!document.title || document.title === "") {
          document.title = defaultTitle;
        }
        // Set favicon: org one if we have it, else default from public folder
        setFavicon(faviconUrl || defaultFaviconHref);
      }
    };
    fetchBranding();
  }, []);

  const value = {
    branding,
    logoUrl: branding?.logoSignedUrl ?? null,
    faviconUrl: branding?.faviconSignedUrl ?? null,
    defaultLogoUrl: DEFAULT_LOGO_URL,
    slug: branding?.slug ?? null,
    loading,
  };

  return (
    <OrganizationBrandingContext.Provider value={value}>
      {children}
    </OrganizationBrandingContext.Provider>
  );
};

export const useOrganizationBranding = () =>
  useContext(OrganizationBrandingContext);
